import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import BottomSheetModal from '../../../components/BottomSheetModal';
import ImageComponent from '../../../components/ImageComponent';
import WithdrawText from '../../Withdraw/components/WithdrawText';
import type {
  IMismatchDocument,
  INameMismatchBottomSheet,
} from '../../../interfaces/Withdrawal';
import styles from '../styles.module.css';

interface NameMismatchSheetProps {
  isOpen: boolean;
  data?: INameMismatchBottomSheet;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Blocks withdrawal when the payout account's name does not match the KYC
 * document, showing both names side by side.
 *
 * Entirely server-driven, including the highlight colours on each name
 * (`subTitle.style.bgColor`) — red for the mismatching account, green for the
 * verified document.
 */
const DocumentRow: React.FC<{ doc?: IMismatchDocument }> = ({ doc }) => {
  if (!doc) return null;

  return (
    <div className={styles.mismatchRow}>
      {doc.iconUrl && (
        <ImageComponent
          src={doc.iconUrl}
          alt=""
          className={styles.mismatchIcon}
        />
      )}
      <div className={styles.mismatchNames}>
        <WithdrawText data={doc.title} className="fontSizeSm12 fontColor666" />
        <span
          className={clsx(styles.mismatchValue, 'fontSizeSm14 fontWeightBold')}
          style={
            doc.subTitle?.style?.bgColor
              ? { backgroundColor: doc.subTitle.style.bgColor }
              : undefined
          }
        >
          {doc.subTitle?.text}
        </span>
      </div>
    </div>
  );
};

const NameMismatchSheet: React.FC<NameMismatchSheetProps> = ({
  isOpen,
  data,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation();
  if (!data) return null;

  const content = (
    <div className={styles.mismatchBody}>
      <WithdrawText
        data={data.subHeading}
        className={clsx(styles.mismatchSubHeading, 'fontWeightBold fontSizeSm')}
      />
      <DocumentRow doc={data.bank} />
      <DocumentRow doc={data.document} />
    </div>
  );

  const actions = [
    {
      label: data.posCta?.displayText || t('continue'),
      onClick: onConfirm,
      btnClass: 'primaryBtn',
    },
    ...(data.negCta?.displayText
      ? [
          {
            label: data.negCta.displayText,
            onClick: onClose,
            btnClass: 'secondaryBtn',
          },
        ]
      : []),
  ];

  return (
    <BottomSheetModal
      isOpen={isOpen}
      title={data.heading?.text}
      content={content}
      actions={actions}
      // `isCancellable: false` means the user must act on the CTA.
      onClose={data.isCancellable === false ? () => {} : onClose}
    />
  );
};

export default NameMismatchSheet;
